import type { FileResponse } from "@efes/shared";

const statusLabels: Record<string, string> = {
	uploaded: "Загружен",
	recognized: "Распознан",
	parsed: "Загружен",
	error: "Ошибка",
};

function formatFileSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatPeriod(month: number | null, year: number | null): string {
	if (!month && !year) return "—";
	const m = month ? String(month).padStart(2, "0") : "??";
	const y = year ?? "????";
	return `${m}/${y}`;
}

function formatDate(value: string): string {
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return value;

	return parsed.toLocaleString("ru-RU", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

interface CurrentRateBookHeaderProps {
	file: FileResponse;
	totalRates: number;
	legacyCount: number;
}

export function CurrentRateBookHeader({
	file,
	totalRates,
	legacyCount,
}: CurrentRateBookHeaderProps) {
	return (
		<div className="space-y-3 rounded-xl border border-slate-200 bg-white p-5">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<p className="text-xs font-medium uppercase tracking-wide text-slate-500">
						Текущая книга расценок
					</p>
					<h2 className="mt-1 text-lg font-semibold text-slate-900">{file.originalName}</h2>
				</div>
				<span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
					{statusLabels[file.status] ?? file.status}
				</span>
			</div>

			{legacyCount > 0 && (
				<div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
					Найдено несколько книг расценок ({legacyCount + 1}). Используется самая новая по дате.
				</div>
			)}

			<div className="grid gap-3 text-sm md:grid-cols-2 xl:grid-cols-4">
				<Item label="ID файла" value={String(file.id)} />
				<Item label="Объект" value={file.objectName ?? "—"} />
				<Item label="Период" value={formatPeriod(file.periodMonth, file.periodYear)} />
				<Item label="Лист" value={file.sheetName ?? "—"} />
				<Item label="Размер" value={formatFileSize(file.fileSize)} />
				<Item label="Дата загрузки" value={formatDate(file.createdAt)} />
				<Item label="Расценок в справочнике" value={totalRates.toLocaleString("ru-RU")} />
				<Item label="Статус обработки" value={statusLabels[file.status] ?? file.status} />
			</div>
		</div>
	);
}

function Item({ label, value }: { label: string; value: string }) {
	return (
		<div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
			<p className="text-xs text-slate-500">{label}</p>
			<p className="mt-1 font-medium text-slate-800">{value}</p>
		</div>
	);
}
